/**
 * TagList — flat index of every active tag on the current page. Hovering a
 * row sets the parent's `hoveredTagId`, which pushes a canvas override
 * (thicker, brighter stroke) so the operator can locate it without
 * scanning. Clicking a row selects the tag.
 *
 * "Active" excludes trash; suggested/confirmed/manual/exception all show.
 */
import { useMemo } from "react";
import { type AnnotationObject } from "@/api/cycles";
import { useSkuCatalog, lookupSku } from "./sku";
import { skuColor } from "@/theme/skuColor";
import { colors } from "@/theme/colors";

type Props = {
  objects: AnnotationObject[];
  hoveredTagId: string | null;
  selectedObjectId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (objectId: string) => void;
};

export function TagList({
  objects,
  hoveredTagId,
  selectedObjectId,
  onHover,
  onSelect,
}: Props) {
  const { data: skus } = useSkuCatalog();

  const visible = useMemo(
    () => objects.filter((o) => o.status !== "trash"),
    [objects],
  );

  if (visible.length === 0) {
    return (
      <div
        style={{
          fontSize: "var(--fs-ui)",
          color: colors.textMuted,
          fontStyle: "italic",
        }}
      >
        Sem tags ativas nesta página.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {visible.map((o) => {
        const tone = o.class ? skuColor(o.class) : null;
        const sku = lookupSku(skus, o.class);
        const isHovered = hoveredTagId === o.id;
        const isSelected = selectedObjectId === o.id;
        const bg = isSelected
          ? colors.panelMuted
          : isHovered
            ? "rgba(0,102,255,0.06)"
            : "transparent";
        return (
          <button
            key={o.id}
            type="button"
            onMouseEnter={() => onHover(o.id)}
            onMouseLeave={() => onHover(null)}
            onFocus={() => onHover(o.id)}
            onBlur={() => onHover(null)}
            onClick={() => onSelect(o.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 6px",
              background: bg,
              border: "none",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              textAlign: "left",
              fontSize: "var(--fs-ui)",
              color: colors.text,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: tone?.fill ?? colors.line,
                flex: "0 0 auto",
              }}
            />
            <span
              className="mono"
              style={{
                minWidth: 90,
                color: o.class ? colors.text : colors.textMuted,
              }}
            >
              {o.class ?? "—"}
            </span>
            <span
              style={{
                flex: 1,
                color: colors.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={sku?.name ?? o.id}
            >
              {sku?.name ?? o.id}
            </span>
          </button>
        );
      })}
    </div>
  );
}
